import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Megaphone, MapPin, Target, Crown, Store, TrendingUp, Eye, MousePointerClick, ShoppingBag } from "lucide-react";
import { toast } from "sonner";

const TYPE_META = {
  sponsored_business: { label: "Sponsored Business", icon: Store, desc: "Attività in evidenza nei risultati" },
  sponsored_map: { label: "Sponsored Map", icon: MapPin, desc: "Pin evidenziato sulla mappa Fuma" },
  sponsored_mission: { label: "Sponsored Mission", icon: Target, desc: "Missione sponsorizzata dalla tua attività" },
  featured_deal: { label: "Featured Deal", icon: Crown, desc: "Offerta Fuma+ in evidenza" },
};
const DAYS = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];

export default function AdvertisingPanel() {
  const [data, setData] = useState(null);
  const [type, setType] = useState("sponsored_business");
  const [form, setForm] = useState({ zones: "", radius_km: 0, categories: "", audience: "all", hour_from: "", hour_to: "", days: [], ref_id: "" });

  const load = () => api.get("/business/ad-campaigns").then(({ data }) => setData(data)).catch(() => {});
  useEffect(() => {
    load();
    const sess = new URLSearchParams(window.location.search).get("ad_session");
    if (!sess) return;
    let n = 0;
    const iv = setInterval(async () => {
      n += 1;
      try { const { data } = await api.get(`/payments/status/${sess}`); if (data.payment_status === "paid") { clearInterval(iv); toast.success("Campagna attivata!"); load(); window.history.replaceState({}, "", "/business/advertising"); } } catch { /* noop */ }
      if (n >= 15) { clearInterval(iv); load(); }
    }, 2000);
    return () => clearInterval(iv);
  }, []);

  const eur = (v) => "€" + Number(v || 0).toFixed(2);
  const toggleDay = (i) => setForm((f) => ({ ...f, days: f.days.includes(i) ? f.days.filter((d) => d !== i) : [...f.days, i] }));

  const createAndPay = async () => {
    try {
      const targeting = {
        zones: form.zones.split(",").map((s) => s.trim()).filter(Boolean),
        radius_km: parseFloat(form.radius_km) || 0,
        categories: form.categories.split(",").map((s) => s.trim()).filter(Boolean),
        audience: form.audience, days: form.days,
        hours: [form.hour_from === "" ? null : parseInt(form.hour_from), form.hour_to === "" ? null : parseInt(form.hour_to)],
      };
      const { data: c } = await api.post("/business/ad-campaigns", { type, targeting, ref_id: form.ref_id || undefined });
      const { data: co } = await api.post(`/business/ad-campaigns/${c.id}/checkout`, { origin_url: window.location.origin });
      if (co.checkout_url) window.location.href = co.checkout_url;
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!data) return <div className="text-zinc-500 py-16 text-center">Caricamento…</div>;
  const price = data.prices?.[type] || {};
  const M = TYPE_META[type];

  return (
    <div className="space-y-5" data-testid="advertising-panel">
      <div>
        <h2 className="font-display font-bold text-xl flex items-center gap-2"><Megaphone className="h-5 w-5 text-[#00E676]" /> Pubblicità locale</h2>
        <p className="text-sm text-zinc-500">Promuovi la tua attività sul territorio. Dimostriamo il ritorno: spesa vs vendite generate.</p>
        {data.advertising_discount_pct > 0 && <p className="text-xs text-[#FFD700] mt-1">Il tuo piano Business include -{data.advertising_discount_pct}% sull'advertising.</p>}
      </div>

      <div className="bg-[#1C1C1F] border border-white/10 rounded-lg p-5 space-y-4">
        <div className="text-sm font-semibold">Nuova campagna</div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {Object.entries(TYPE_META).map(([k, m]) => {
            const Ic = m.icon;
            return (
              <button key={k} data-testid={`ad-type-${k}`} onClick={() => setType(k)} className={`rounded-lg border p-3 text-left ${type === k ? "border-[#00E676] bg-[#00E676]/10" : "border-white/10 hover:border-white/20"}`}>
                <Ic className="h-4 w-4 text-[#00E676] mb-1" />
                <div className="text-xs font-semibold">{m.label}</div>
              </button>
            );
          })}
        </div>
        <p className="text-xs text-zinc-500">{M.desc}</p>
        {(type === "sponsored_mission" || type === "featured_deal") && (
          <div><Label className="text-xs">ID {type === "featured_deal" ? "Offerta (Deal)" : "Missione"}</Label><Input data-testid="ad-refid" value={form.ref_id} onChange={(e) => setForm((f) => ({ ...f, ref_id: e.target.value }))} className="bg-[#0A0A0C] border-white/10 mt-1 h-9" /></div>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div><Label className="text-xs">Comuni (virgola, vuoto = tutti)</Label><Input value={form.zones} onChange={(e) => setForm((f) => ({ ...f, zones: e.target.value }))} className="bg-[#0A0A0C] border-white/10 mt-1 h-9" /></div>
          <div><Label className="text-xs">Raggio (km)</Label><Input type="number" value={form.radius_km} onChange={(e) => setForm((f) => ({ ...f, radius_km: e.target.value }))} className="bg-[#0A0A0C] border-white/10 mt-1 h-9" /></div>
          <div><Label className="text-xs">Categorie (virgola)</Label><Input value={form.categories} onChange={(e) => setForm((f) => ({ ...f, categories: e.target.value }))} className="bg-[#0A0A0C] border-white/10 mt-1 h-9" /></div>
          <div><Label className="text-xs">Pubblico</Label>
            <select data-testid="ad-audience" value={form.audience} onChange={(e) => setForm((f) => ({ ...f, audience: e.target.value }))} className="w-full mt-1 bg-[#0A0A0C] border border-white/10 rounded-md h-9 px-3 text-sm">
              <option value="all">Tutti</option><option value="free">Solo Free</option><option value="plus">Solo Fuma+</option><option value="new">Nuovi clienti</option><option value="existing">Clienti esistenti</option>
            </select>
          </div>
          <div><Label className="text-xs">Orario da (0-23)</Label><Input type="number" value={form.hour_from} onChange={(e) => setForm((f) => ({ ...f, hour_from: e.target.value }))} className="bg-[#0A0A0C] border-white/10 mt-1 h-9" /></div>
          <div><Label className="text-xs">Orario a (0-23)</Label><Input type="number" value={form.hour_to} onChange={(e) => setForm((f) => ({ ...f, hour_to: e.target.value }))} className="bg-[#0A0A0C] border-white/10 mt-1 h-9" /></div>
        </div>
        <div>
          <Label className="text-xs">Giorni</Label>
          <div className="flex gap-1 mt-1 flex-wrap">
            {DAYS.map((d, i) => <button key={i} onClick={() => toggleDay(i)} className={`px-2.5 py-1 rounded-full text-xs ${form.days.includes(i) ? "bg-[#00E676] text-black" : "bg-white/5 text-zinc-400"}`}>{d}</button>)}
          </div>
        </div>
        <div className="flex items-center justify-between flex-wrap gap-2 border-t border-white/10 pt-3">
          <div className="text-sm">Prezzo: {price.discount_pct > 0 && <span className="text-zinc-500 line-through mr-2">{eur(price.base)}</span>}<b className="text-[#00E676] text-lg" data-testid="ad-price">{eur(price.price)}</b> <span className="text-xs text-zinc-500">/ {type === "sponsored_mission" ? "campagna (30gg)" : "settimana"}</span></div>
          <Button data-testid="ad-create-pay" onClick={createAndPay} className="bg-[#00E676] text-black rounded-md font-semibold">Crea e attiva campagna</Button>
        </div>
      </div>

      <div className="space-y-2">
        <div className="text-sm font-semibold">Le mie campagne</div>
        {(data.campaigns || []).map((c) => (
          <div key={c.id} data-testid={`ad-campaign-${c.id}`} className="bg-[#1C1C1F] border border-white/10 rounded-lg p-4">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="font-semibold flex items-center gap-2">{TYPE_META[c.type]?.label || c.type}
                <span className={`text-[10px] rounded-full px-2 py-0.5 ${c.effective_status === "active" ? "bg-[#00E676]/20 text-[#00E676]" : "bg-white/10 text-zinc-400"}`}>{c.effective_status}</span></div>
              {c.effective_status !== "active" && c.status === "draft" && <Button size="sm" data-testid={`ad-pay-${c.id}`} onClick={async () => { const { data } = await api.post(`/business/ad-campaigns/${c.id}/checkout`, { origin_url: window.location.origin }); if (data.checkout_url) window.location.href = data.checkout_url; }} className="bg-[#00E676] text-black rounded-md h-8 text-xs">Paga e attiva</Button>}
            </div>
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mt-3 text-center">
              <div><div className="text-sm font-bold flex items-center justify-center gap-1"><Eye className="h-3 w-3 text-zinc-500" />{c.analytics.impressions}</div><div className="text-[9px] uppercase text-zinc-500">Impression</div></div>
              <div><div className="text-sm font-bold flex items-center justify-center gap-1"><MousePointerClick className="h-3 w-3 text-zinc-500" />{c.analytics.clicks}</div><div className="text-[9px] uppercase text-zinc-500">Click</div></div>
              <div><div className="text-sm font-bold">{c.analytics.ctr}%</div><div className="text-[9px] uppercase text-zinc-500">CTR</div></div>
              <div><div className="text-sm font-bold flex items-center justify-center gap-1"><ShoppingBag className="h-3 w-3 text-zinc-500" />{c.analytics.orders}</div><div className="text-[9px] uppercase text-zinc-500">Ordini</div></div>
              <div><div className="text-sm font-bold">{eur(c.analytics.gmv)}</div><div className="text-[9px] uppercase text-zinc-500">GMV</div></div>
              <div><div className="text-sm font-bold text-[#FFD700] flex items-center justify-center gap-1"><TrendingUp className="h-3 w-3" />{c.analytics.roas}x</div><div className="text-[9px] uppercase text-zinc-500">ROAS</div></div>
            </div>
          </div>
        ))}
        {(data.campaigns || []).length === 0 && <div className="text-zinc-500 py-6 text-center text-sm">Nessuna campagna ancora.</div>}
      </div>
    </div>
  );
}
